import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import { Collapse, Typography } from '@mui/material';
import { useId, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { formatCurrency } from '@/shared/lib/currency';

import type { CompanyGroup } from './grouping';
import { ItemCard } from './ItemCard';
import type { OutstandingItem } from './outstanding.schema';
import type { OutstandingAction } from './rowActions';

/**
 * Everything one company is waiting on, collapsed until asked for.
 *
 * A director with six companies opened this screen onto every item of every one
 * of them, which on a phone is several screens of scrolling before they know
 * how many companies there even are. Closed, the page is a list of companies
 * with what each owes stated in the header — the summary line and the amount
 * are the whole point of the header, and they say enough to decide which
 * company to open without opening any of them.
 *
 * The header is one control: anywhere on it opens the group. The company name
 * used to be a link to the detail screen, which cannot survive inside a button
 * — the register is the route to a company, and this screen is about the work,
 * not the company record.
 */

/** The header's own summary line — what this company needs, in words, before
 *  any card is read. Ordered by what it asks of the reader: money first, since
 *  that is the leg with a deadline behind it, then signatures, then the items
 *  that ask nothing at all. */
const summaryOf = (group: CompanyGroup): string => {
  const parts: string[] = [];
  if (group.toPay > 0) {
    parts.push(group.toPay === 1 ? '1 to pay' : `${String(group.toPay)} to pay`);
  }
  if (group.toSign > 0) {
    parts.push(group.toSign === 1 ? '1 to sign' : `${String(group.toSign)} to sign`);
  }
  // Stated in the closed header because it is the one state the director cannot
  // act on: leaving it inside the group means the answer to "have I finished
  // with Acme?" is only reachable by expanding Acme. It sits last — it is
  // reassurance, not a task, and must never lead a line that also has work in
  // it.
  if (group.waiting > 0) {
    parts.push(
      group.waiting === 1
        ? '1 awaiting co-directors'
        : `${String(group.waiting)} awaiting co-directors`,
    );
  }
  // Reached only by a group with no items at all; every item lands in one of
  // the three counts above.
  return parts.length === 0 ? 'Nothing needs you right now' : parts.join(' · ');
};

/** What opening the group reveals, said as a count — the toggle's accessible
 *  name, where "2 items" is what a screen reader needs and the summary line's
 *  breakdown is not. */
const contentsOf = (group: CompanyGroup): string =>
  group.items.length === 1 ? '1 item' : `${String(group.items.length)} items`;

interface CompanyGroupCardProps {
  group: CompanyGroup;
  onAction: (item: OutstandingItem, action: OutstandingAction) => void;
  onStart: (item: OutstandingItem) => void;
}

export const CompanyGroupCard = ({ group, onAction, onStart }: CompanyGroupCardProps) => {
  // Every group starts closed, including the overdue one. Auto-opening the
  // urgent company would put this screen back where it started for the director
  // whose companies are all overdue, and the header already says it is late.
  const [expanded, setExpanded] = useState(false);
  const headingId = `company-${group.companyId}`;
  const itemsId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden rounded-3xl border border-divider bg-surface shadow-card"
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={itemsId}
        aria-label={`${expanded ? 'Hide' : 'Show'} ${contentsOf(group)} for ${group.companyName}`}
        onClick={() => {
          setExpanded(!expanded);
        }}
        className={cn(
          'flex w-full items-center gap-3 px-4 py-3.5 text-left',
          'cursor-pointer border-0 border-solid',
          'outline-none focus-visible:ring-2 focus-visible:ring-primary-dark focus-visible:ring-inset',
          // A real edge, not just a tint. The header sits directly on the white
          // item list, and a pale fill alone has too little luminance against it
          // to read as a separate band — the border is what separates them.
          // Only while open: closed, there is nothing below it to divide.
          expanded && 'border-b',
          // Both states are grey: the header names the company, and the company
          // is not the thing that is late. Lateness is already carried by each
          // item's own red due line, which is where it belongs. The overdue
          // group takes the deeper rung so it still leads the page at a glance
          // — a difference in weight, which survives greyscale, rather than one
          // in hue, which would spend the app's only colour signal on chrome.
          group.hasOverdue ? 'border-brand-300 bg-brand-100' : 'border-brand-200 bg-brand-50',
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* Plain text, not a link: the header is one control now, and a link
              inside a button is invalid markup that also makes one target do
              two different things depending on where a thumb lands. */}
          <Typography
            id={headingId}
            variant="body2"
            component="h2"
            className="font-bold text-primary-dark"
          >
            {group.companyName}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {summaryOf(group)}
          </Typography>
        </div>

        {group.amountDue > 0 && (
          <Typography variant="body2" className="shrink-0 font-bold tabular-nums">
            {formatCurrency(group.amountDue)}
          </Typography>
        )}

        {/* Decoration now — the whole header is the control, so the chevron
            reports the state rather than owning it. Kept at the 44px width its
            own button had, so the header's right edge did not shift. */}
        <span className="-mr-2 flex size-11 shrink-0 items-center justify-center text-primary-dark">
          <ExpandMoreIcon
            className={cn('transition-transform duration-200', expanded && 'rotate-180')}
          />
        </span>
      </button>

      <Collapse in={expanded} unmountOnExit>
        <ul id={itemsId} className="m-0 list-none p-0">
          {group.items.map((item) => (
            <ItemCard key={item.id} item={item} onAction={onAction} onStart={onStart} />
          ))}
        </ul>
      </Collapse>
    </section>
  );
};
